import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import { Button, FormInput, DropDown } from "@src/components/atoms";
import { useIsMobileWidth } from "@src/utils";
import Form from "./Form";
import { buildCrewUpdateSchema } from "./formHelpers";

const ROLE_OPTIONS = [
  { value: "MASTER", text: "Master" },
  { value: "CHIEF_ENGINEER", text: "Chief Engineer" },
];

const CrewUpdateForm = ({
  onNotifySignol,
  vesselName,
  imo,
  employeeIdSettings,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useIsMobileWidth();

  const showVesselImo = !vesselName || !imo;

  // Values reaching handleSubmit are already cleaned by the schema — it applies
  // the same normalisation as the crew_rotations_job pipeline.
  const schema = useMemo(
    () => buildCrewUpdateSchema(showVesselImo, employeeIdSettings?.label),
    [showVesselImo, employeeIdSettings?.label],
  );

  const handleSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      if (onNotifySignol) await onNotifySignol(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto p-6">
      <div className={`mb-8 ${isMobile ? "" : "px-6"}`}>
        <h3 className="mb-2 text-primary-500">Crew Update</h3>
        <p>Please complete all fields.</p>
      </div>
      <Form
        id="crew-update"
        onSubmit={handleSubmit}
        schema={schema}
        className="space-y-6"
        loading={isSubmitting}
        options={{ defaultValues: { role: "MASTER" } }}
      >
        {(methods) => (
          <div className={isMobile ? "" : "px-6"}>
            <div className="flex flex-col gap-6 mb-4">
              <div
                className={`${isMobile ? "flex flex-col" : "grid grid-cols-2"} gap-6`}
              >
                <FormInput
                  label="First Name"
                  name="firstname"
                  type="text"
                  placeholder="John"
                  textColor="primary"
                  required
                  error={methods.formState.errors.firstname?.message}
                />
                <FormInput
                  label="Last Name"
                  name="lastname"
                  type="text"
                  placeholder="Smith"
                  textColor="primary"
                  required
                  error={methods.formState.errors.lastname?.message}
                />
              </div>
              <div
                className={`${isMobile ? "flex flex-col" : "grid grid-cols-2"} gap-6`}
              >
                <div className="w-full">
                  <label className="block text-sm font-bold text-primary-500 mb-2">
                    Role <span>{" * "}</span>
                  </label>
                  <Controller
                    name="role"
                    control={methods.control}
                    render={({ field }) => (
                      <DropDown
                        fullWidth
                        options={ROLE_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        errorState={!!methods.formState.errors.role}
                      />
                    )}
                  />
                  {methods.formState.errors.role?.message && (
                    <p className="mt-1 text-xs text-error">
                      {methods.formState.errors.role.message}
                    </p>
                  )}
                </div>
                <FormInput
                  label={employeeIdSettings?.label ?? "Employee ID"}
                  info={employeeIdSettings?.tooltip}
                  name="employeeId"
                  type="text"
                  placeholder={employeeIdSettings?.example ?? "EMP-12345"}
                  textColor="primary"
                  required
                  error={methods.formState.errors.employeeId?.message}
                />
              </div>
              <div
                className={`${isMobile || !showVesselImo ? "flex flex-col" : "grid grid-cols-2"} gap-6`}
              >
                {showVesselImo && (
                  <FormInput
                    label="Vessel IMO"
                    name="vesselImo"
                    type="text"
                    placeholder="1234567"
                    textColor="primary"
                    required
                    error={methods.formState.errors.vesselImo?.message}
                  />
                )}
                <FormInput
                  label={`Joined ${vesselName ?? "the vessel"} on`}
                  name="joinedVesselOn"
                  type="date"
                  textColor="primary"
                  required
                  error={methods.formState.errors.joinedVesselOn?.message}
                />
              </div>
            </div>
            <div
              className={`${isMobile ? "flex flex-col items-center gap-4" : "flex justify-center items-center"} mt-6`}
            >
              <div className={isMobile ? "w-full" : ""}>
                <Button padding={false} submit loading={isSubmitting}>
                  Notify Signol
                </Button>
              </div>
            </div>
          </div>
        )}
      </Form>
    </div>
  );
};

CrewUpdateForm.propTypes = {
  onNotifySignol: PropTypes.func,
  vesselName: PropTypes.string,
  imo: PropTypes.string,
  employeeIdSettings: PropTypes.shape({
    label: PropTypes.string,
    example: PropTypes.string,
    tooltip: PropTypes.string,
    key: PropTypes.string,
  }),
};

export default CrewUpdateForm;
